import { useCallback } from 'react';

import { useBoothLogout, useBoothSessionForceRefresh, useBoothUserLogout, useUser } from '@auth-v2';
import { useAtomValue } from 'jotai';
import { startCase } from 'lodash';
import { useNavigate } from 'react-router';

import { useTranslate } from '@i18n';
import { useDisableLiveAnalysis, useLiveAnalysisEnabled } from '@queries';
import { isActiveCurrentUserAtom, userAtom, studentIdAtom, activityAtom } from '@store';
import {
    Menu,
    MenuItem,
    ClockCounterClockwiseIcon,
    GearIcon,
    SignOutIcon,
    XCircleIcon,
    GoalIcon,
    StudentIcon,
    Text,
} from '@ui';

import { LINKS } from '../../utils/routes';

import * as css from './Navigation.css';

export const MenuLabel = () => {
    const t = useTranslate();
    const { data: user } = useUser();
    return (
        <Text variant="h3">
            {user?.user_metadata?.['name'] ? startCase(user?.user_metadata?.['name']) : t('menu.actions')}
        </Text>
    );
};

export const NavigationMenu = () => {
    const navigate = useNavigate();

    const t = useTranslate();
    const { mutateAsync: logout } = useBoothUserLogout();
    const { mutateAsync: logoutBooth } = useBoothLogout();
    const { mutateAsync: forceRefresh } = useBoothSessionForceRefresh();
    const { data: liveAnalysisStatus } = useLiveAnalysisEnabled();
    const disableLiveAnalysis = useDisableLiveAnalysis();

    const handleLogout: React.MouseEventHandler = useCallback(async () => {
        if (liveAnalysisStatus?.enabled) {
            await disableLiveAnalysis.mutateAsync();
        }
        logout();
    }, [disableLiveAnalysis, liveAnalysisStatus?.enabled, logout]);

    const handleLogoutBooth: React.MouseEventHandler = useCallback(async () => {
        if (liveAnalysisStatus?.enabled) {
            await disableLiveAnalysis.mutateAsync();
        }
        logoutBooth();
    }, [disableLiveAnalysis, liveAnalysisStatus?.enabled, logoutBooth]);

    const handleRefresh: React.MouseEventHandler = useCallback(async () => {
        await forceRefresh();
        window.location.reload();
    }, [forceRefresh]);

    const activity = useAtomValue(activityAtom);

    const isActiveCurrentUser = useAtomValue(isActiveCurrentUserAtom);
    const user = useAtomValue(userAtom);
    const isModerator = user?.user_metadata['is_admin'];

    const studentId = useAtomValue(studentIdAtom);

    return (
        <Menu label="Actions" align="left" buttonSize={24} menuType="main" className={css.menu}>
            <MenuItem onClick={() => navigate(LINKS.getUserSettings())} style={{ justifyContent: 'flex-start' }}>
                <GearIcon size={18} />
                <Text variant="p">{t('menu.user_settings')}</Text>
            </MenuItem>
            {!isActiveCurrentUser && (
                <MenuItem onClick={() => navigate(LINKS.getStudentSettings())} style={{ justifyContent: 'flex-start' }}>
                    <StudentIcon size={18} />
                    <Text variant="p">{t('menu.student_settings')}</Text>
                </MenuItem>
            )}
            <MenuItem onClick={() => navigate(LINKS.getOverview(studentId))} style={{ justifyContent: 'flex-start' }}>
                <ClockCounterClockwiseIcon size={18} />
                <Text variant="p">{t('menu.activity_overview')}</Text>
            </MenuItem>
            {activity?.layoutId && (
                <MenuItem
                    onClick={() => navigate(LINKS.getGoalReport(activity.layoutId!))}
                    style={{ justifyContent: 'flex-start' }}
                >
                    <GoalIcon size={18} color="currentColor" />
                    <Text variant="p">{t('menu.goal_report')}</Text>
                </MenuItem>
            )}
            {(activity?.id ?? -1) >= 0 && (
                <MenuItem onClick={() => navigate(LINKS.getReport())} style={{ justifyContent: 'flex-start' }}>
                    <GoalIcon size={18} color="currentColor" />
                    <Text variant="p">{t('menu.activity_report')}</Text>
                </MenuItem>
            )}
            <MenuItem onClick={handleLogout} style={{ justifyContent: 'flex-start' }}>
                <SignOutIcon size={18} />
                <Text variant="p">{t('menu.end_session')}</Text>
            </MenuItem>

            {isModerator && (
                <MenuItem onClick={handleLogoutBooth} style={{ justifyContent: 'flex-start' }}>
                    <XCircleIcon weight="fill" size={18} />
                    <Text variant="p">{t('menu.end_booth_session')}</Text>
                </MenuItem>
            )}

            <MenuItem onClick={handleRefresh} style={{ justifyContent: 'flex-start' }}>
                <XCircleIcon size={18} />
                <Text variant="p">{t('menu.refresh')}</Text>
            </MenuItem>
        </Menu>
    );
};
